import React from 'react';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  isChunkError: boolean;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Detect chunk loading errors
    const isChunkError = 
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Failed to load module script');
    
    console.error('ErrorBoundary caught error:', error);
    
    return { 
      hasError: true, 
      error,
      isChunkError,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary details:', { error, errorInfo });
    
    // Auto-reload on chunk errors (only once per session)
    if (this.state.isChunkError && !sessionStorage.getItem('error_boundary_reload')) {
      sessionStorage.setItem('error_boundary_reload', 'true');
      console.log('🔄 Chunk loading error in error boundary, reloading...');
      
      // Clear version to force re-check
      localStorage.removeItem('app_version');
      
      // Reload after a brief delay
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined, isChunkError: false });
    sessionStorage.removeItem('error_boundary_reload');
    sessionStorage.removeItem('chunk-reload');
  };

  render() {
    if (this.state.hasError) {
      // Special UI for chunk errors
      if (this.state.isChunkError) {
        return <ChunkErrorFallback resetError={this.resetError} />;
      }
      
      // Regular error UI
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

const ChunkErrorFallback: React.FC<{ resetError: () => void }> = ({ resetError }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center p-8 max-w-md">
      <RefreshCw className="w-16 h-16 mx-auto mb-4 text-blue-600 animate-spin" />
      <h2 className="text-2xl font-bold text-gray-900 mb-4">App Update Available</h2>
      <p className="text-gray-600 mb-6">
        We're loading the latest version. This page will refresh automatically...
      </p>
      <Button onClick={() => window.location.reload()} size="lg">
        Reload Now
      </Button>
      <button 
        onClick={resetError}
        className="mt-4 text-sm text-gray-500 hover:text-gray-700 underline"
      >
        Try without reloading
      </button>
    </div>
  </div>
);

const DefaultErrorFallback: React.FC<{ error?: Error; resetError: () => void }> = ({ error, resetError }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center p-8 max-w-md">
      <h2 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h2>
      <p className="text-gray-600 mb-4">{error?.message || 'An unexpected error occurred'}</p>
      <div className="space-y-2">
        <Button onClick={resetError} className="w-full">Try again</Button>
        <Button 
          onClick={() => window.location.reload()} 
          variant="outline"
          className="w-full"
        >
          Reload page
        </Button>
      </div>
    </div>
  </div>
);

export default ErrorBoundary;